/* 💥 BattleFx — damage floats, crit shake, hit flash, kill-streak audio
 * -------------------------------------------------------------------
 * Attach one <BattleFxLayer/> next to the Battlegrounds map. Then call the
 * exported `battleFx.hit({x, y, damage, isCrit, isBlock, isKill})` and
 * `battleFx.streak(name, count)` helpers from combat handlers.
 *
 * Everything is CSS + Web Audio API — no external libs.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";

// ─── Tiny event bus ─────────────────────────────────────────────────
const listeners = { hit: [], streak: [], shake: [] };
export const battleFx = {
  hit:    (payload) => listeners.hit.forEach(fn => fn(payload)),
  streak: (name, count) => listeners.streak.forEach(fn => fn({ name, count })),
  shake:  (ms = 220, magnitude = 6) => listeners.shake.forEach(fn => fn({ ms, magnitude })),
};
function useBusListener(kind, fn) {
  useEffect(() => {
    listeners[kind].push(fn);
    return () => { listeners[kind] = listeners[kind].filter(x => x !== fn); };
  }, [kind, fn]);
}

// ─── Web-Audio callout synth (no deps) ─────────────────────────────
// Plays a short 3-tone rising fanfare. Frequency scales with streak intensity.
let _ac = null;
function ensureAudio() {
  if (_ac) return _ac;
  try {
    // eslint-disable-next-line no-undef
    _ac = new (window.AudioContext || window.webkitAudioContext)();
  } catch { _ac = null; }
  return _ac;
}
function playCallout(intensity = 1) {
  const ac = ensureAudio();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  const baseFreqs = [440, 660, 880];         // A5, E6, A6
  const freqs = baseFreqs.map(f => f * (1 + 0.08 * (intensity - 1)));
  freqs.forEach((f, i) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = i === 2 ? "sawtooth" : "triangle";
    o.frequency.setValueAtTime(f, now + i * 0.08);
    g.gain.setValueAtTime(0.0001, now + i * 0.08);
    g.gain.exponentialRampToValueAtTime(0.18, now + i * 0.08 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.08 + 0.24);
    o.connect(g).connect(ac.destination);
    o.start(now + i * 0.08);
    o.stop(now + i * 0.08 + 0.28);
  });
}

// Public streak-thresholds (canonical names → audio intensity)
export const KILL_STREAK_TIERS = [
  { at: 2,  name: "DOUBLE KILL",  color: "#F59E0B", intensity: 1 },
  { at: 3,  name: "TRIPLE KILL",  color: "#EF4444", intensity: 2 },
  { at: 5,  name: "RAMPAGE",      color: "#A855F7", intensity: 3 },
  { at: 8,  name: "DOMINATING",   color: "#EAB308", intensity: 4 },
  { at: 12, name: "GODLIKE",      color: "#F5D96B", intensity: 5 },
];
export function streakTierFor(count) {
  let hit = null;
  for (const t of KILL_STREAK_TIERS) if (count >= t.at) hit = t;
  return hit;
}

// ─── Floating damage number ────────────────────────────────────────
function DamageFloat({ hit, onDone }) {
  useEffect(() => {
    const id = setTimeout(onDone, 950);
    return () => clearTimeout(id);
  }, [onDone]);
  const color = hit.isKill  ? "#F5D96B"
             : hit.isCrit  ? "#FBBF24"
             : hit.isBlock ? "#94A3B8"
                           : "#F87171";
  const label = hit.isBlock ? `${hit.damage} 🛡️` : `${hit.damage}${hit.isCrit ? "!" : ""}`;
  const size  = hit.isKill ? 32 : hit.isCrit ? 26 : 20;
  return (
    <div
      data-testid={`damage-float${hit.isCrit ? "-crit" : ""}`}
      className="pointer-events-none absolute font-black select-none"
      style={{
        left: hit.x, top: hit.y,
        color,
        fontSize: size,
        textShadow: "0 2px 6px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.7)",
        animation: `battle-float ${hit.isCrit ? "0.95s" : "0.8s"} ease-out forwards`,
        zIndex: 60,
      }}
    >
      {label}
    </div>
  );
}

// ─── Streak banner ──────────────────────────────────────────────────
function StreakBanner({ item, onDone }) {
  useEffect(() => {
    const id = setTimeout(onDone, 2000);
    return () => clearTimeout(id);
  }, [onDone]);
  return (
    <div
      data-testid="streak-banner"
      className="pointer-events-none absolute left-1/2 top-8 -translate-x-1/2 z-70 select-none"
      style={{
        animation: "streak-slam 2.0s cubic-bezier(.2,.9,.2,1) forwards",
      }}
    >
      <div
        className="px-6 py-3 rounded-2xl border-2 font-black uppercase tracking-[0.35em] text-2xl md:text-4xl"
        style={{
          color: item.color,
          borderColor: item.color,
          background: "rgba(0,0,0,0.75)",
          textShadow: `0 0 18px ${item.color}, 0 2px 4px #000`,
          boxShadow: `0 0 28px ${item.color}55, 0 0 4px ${item.color}88 inset`,
        }}
      >
        {item.name}
      </div>
    </div>
  );
}

// ─── The layer component ───────────────────────────────────────────
export default function BattleFxLayer() {
  const [hits, setHits] = useState([]);
  const [banner, setBanner] = useState(null);
  const [shake, setShake] = useState(null);
  const wrapRef = useRef(null);
  const nextId = useRef(1);

  useBusListener("hit", useCallback((payload) => {
    const id = nextId.current++;
    setHits(prev => [...prev, { id, ...payload }]);
    if (payload.isCrit) battleFx.shake(260, 8);
    else if (payload.isKill) battleFx.shake(360, 10);
  }, []));

  useBusListener("streak", useCallback(({ name, count }) => {
    const tier = streakTierFor(count) || { name, color: "#F59E0B", intensity: 1 };
    setBanner({ ...tier, name: name || tier.name });
    playCallout(tier.intensity);
    battleFx.shake(280, 6);
  }, []));

  useBusListener("shake", useCallback(({ ms, magnitude }) => {
    setShake({ ms, magnitude, at: Date.now() });
    const to = setTimeout(() => setShake(null), ms);
    return () => clearTimeout(to);
  }, []));

  const removeHit    = (id) => setHits(prev => prev.filter(h => h.id !== id));
  const clearBanner  = () => setBanner(null);

  // Apply shake to the wrapping div
  const shakeStyle = shake ? {
    animation: `battle-shake ${shake.ms}ms cubic-bezier(.36,.07,.19,.97)`,
    animationIterationCount: 1,
    "--shake-mag": `${shake.magnitude}px`,
  } : {};

  return (
    <>
      {/* Keyframes injected once */}
      <style>{`
        @keyframes battle-float {
          0%   { transform: translate(-50%, 0)    scale(0.7); opacity: 0; }
          15%  { transform: translate(-50%, -6px) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, -60px) scale(1.0); opacity: 0; }
        }
        @keyframes streak-slam {
          0%   { transform: translate(-50%, -20px) scale(0.4); opacity: 0; filter: blur(6px); }
          15%  { transform: translate(-50%, 0)     scale(1.15); opacity: 1; filter: blur(0); }
          25%  { transform: translate(-50%, 0)     scale(1.0);  opacity: 1; }
          85%  { transform: translate(-50%, 0)     scale(1.0);  opacity: 1; }
          100% { transform: translate(-50%, -14px) scale(0.9);  opacity: 0; }
        }
        @keyframes battle-shake {
          10%,90% { transform: translate3d(calc(var(--shake-mag) * -1), 0, 0); }
          20%,80% { transform: translate3d(var(--shake-mag), 0, 0); }
          30%,50%,70% { transform: translate3d(calc(var(--shake-mag) * -0.6), 0, 0); }
          40%,60% { transform: translate3d(calc(var(--shake-mag) * 0.6), 0, 0); }
        }
        [data-testid="bg-arena-map"] {
          transform: translateZ(0);
        }
      `}</style>
      <div
        ref={wrapRef}
        className="pointer-events-none absolute inset-0 z-40 overflow-hidden"
        style={shakeStyle}
        data-testid="battle-fx-layer"
      >
        {hits.map(h => <DamageFloat key={h.id} hit={h} onDone={() => removeHit(h.id)} />)}
        {banner && <StreakBanner item={banner} onDone={clearBanner} />}
      </div>
    </>
  );
}
